import { useMutation } from '@tanstack/react-query';

import type { components } from 'wg-api-sdk';

import useAuth from '@/hooks/useAuth';
import { getHeaders } from '@/lib/api/authMiddleware';

type CreateDocumentForDistributionInput = {
  filename: string;
  description: string;
  isHiddenForCustomer: boolean;
  tags?: string[];
  type?: components['schemas']['DOCUMENT_TYPE'];
};

type CreateDocumentForDistributionResult = {
  document_id: string;
  link: string;
  public_url: string;
};

export default function useCreateDocumentForDistribution() {
  const { broker } = useAuth();

  return useMutation({
    mutationFn: async ({
      filename,
      description,
      isHiddenForCustomer,
      tags,
      type,
    }: CreateDocumentForDistributionInput): Promise<CreateDocumentForDistributionResult> => {
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_HOST;
      const url = `${baseUrl}/v1/api/broker/v1/api/distributions/${broker?.distribution_id}/uploads`;

      const result = await fetch(url, {
        method: 'POST',
        headers: {
          ...getHeaders(),
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          filename,
          description,
          hidden_for_customer: isHiddenForCustomer,
          tags,
          type,
        }),
      });

      if (!result.ok) {
        const errorBody = await result.text();

        throw new Error(
          `Creating document for distribution failed (${result.status}): ${errorBody}`,
        );
      }

      return result.json();
    },
  });
}
